import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Search, Package, ChevronDown, PackagePlus } from "lucide-react";
import { productAPI, productFamilyAPI, productSubFamilyAPI } from "@/lib/api";

function formatPrice(v) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
}

const collator = new Intl.Collator("fr", { sensitivity: "base" });

export default function ProductPicker({ value, onSelect, onCreateNew }) {
  const [query, setQuery] = useState(value || "");
  const [families, setFamilies] = useState([]);
  const [subFamilies, setSubFamilies] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [familyId, setFamilyId] = useState("");
  const [subFamilyId, setSubFamilyId] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    productFamilyAPI.list().then(r => setFamilies((r.data || []).sort((a, b) => collator.compare(a.name || "", b.name || "")))).catch(() => {});
    productSubFamilyAPI.list().then(r => setSubFamilies((r.data || []).sort((a, b) => collator.compare(a.name || "", b.name || "")))).catch(() => {});
    productAPI.list({ limit: 5000 }).then(r => setAllProducts(r.data.items || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { setQuery(value || ""); }, [value]);

  const filterProducts = useCallback((q, fam, sfam) => {
    let prods = allProducts;
    if (fam) prods = prods.filter(p => p.family_id === fam);
    if (sfam) prods = prods.filter(p => p.sub_family_id === sfam);
    if (q && q.length >= 1) {
      const lower = q.toLowerCase();
      prods = prods.filter(p => p.name?.toLowerCase().includes(lower));
    }
    return [...prods].sort((a, b) => collator.compare(a.name || "", b.name || ""));
  }, [allProducts]);

  useEffect(() => {
    if (open) setResults(filterProducts(query, familyId, subFamilyId));
  }, [query, familyId, subFamilyId, open, filterProducts]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = (product) => {
    setQuery(product.name);
    setOpen(false);
    onSelect(product);
  };

  const handleFamilyChange = (e) => {
    setFamilyId(e.target.value);
    setSubFamilyId("");
  };

  const filteredSubs = familyId ? subFamilies.filter(s => s.family_id === familyId) : subFamilies;

  return (
    <div ref={ref} className="relative" data-testid="product-picker">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Rechercher un produit..."
          className="h-8 text-sm pl-8"
          title={query}
          data-testid="product-picker-input"
        />
      </div>
      {open && (
        <div
          className="absolute top-full left-0 w-[520px] max-w-[85vw] min-w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-[9999] max-h-[300px] overflow-hidden"
          data-testid="product-picker-dropdown"
          onMouseDown={e => e.stopPropagation()}
        >
          <div className="flex gap-1 p-1.5 border-b border-slate-100 bg-slate-50">
            <div className="relative flex-1">
              <select
                value={familyId}
                onChange={handleFamilyChange}
                className="w-full h-7 text-xs border border-slate-200 rounded px-2 pr-6 bg-white appearance-none cursor-pointer"
                data-testid="family-filter"
              >
                <option value="">Toutes familles</option>
                {families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <div className="relative flex-1">
              <select
                value={subFamilyId}
                onChange={e => setSubFamilyId(e.target.value)}
                className="w-full h-7 text-xs border border-slate-200 rounded px-2 pr-6 bg-white appearance-none cursor-pointer"
                data-testid="subfamily-filter"
              >
                <option value="">Toutes sous-familles</option>
                {filteredSubs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="overflow-y-auto max-h-[240px]">
            {results.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">Aucun produit trouvé</div>
            )}
            {results.map(p => {
              const price = p.unit_price || p.sale_price_ht || 0;
              return (
                <button
                  key={p.id}
                  onClick={() => pick(p)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-indigo-50 text-left transition-colors border-b border-slate-50 last:border-0"
                  data-testid={`product-option-${p.id}`}
                >
                  <Package size={13} className="text-slate-400 shrink-0" />
                  <span className="flex-1 text-sm text-slate-800 truncate">{p.name}</span>
                  {price > 0 && <span className="text-xs font-medium text-indigo-600 shrink-0 whitespace-nowrap ml-2">{formatPrice(price)}</span>}
                </button>
              );
            })}
            {onCreateNew && query.trim().length > 0 && (
              <button
                type="button"
                onClick={() => { setOpen(false); onCreateNew(query.trim()); }}
                className="w-full flex items-center gap-2 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-left transition-colors border-t border-emerald-200"
                data-testid="product-picker-create-new"
              >
                <PackagePlus size={14} className="text-emerald-600 shrink-0" />
                <span className="flex-1 text-sm text-emerald-800">Créer le produit « {query.trim()} »</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
